import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { BreadcrumbProvider } from '@/components/common/BreadcrumbContext';
import { SalesSidebar } from '@/components/sales/SalesSidebar';
import { SalesTopBar } from '@/components/sales/SalesTopBar';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useIsDesktop } from '@/hooks/useMediaQuery';

const SalesLayout = () => {
  const isDesktop = useIsDesktop();
  const [collapsed, setCollapsed] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Crossing up into desktop must dismiss the drawer, or it stays mounted over
  // the rail that has just appeared behind it.
  useEffect(() => {
    if (isDesktop) setNavOpen(false);
  }, [isDesktop]);

  // Below lg the same control opens the drawer instead of narrowing the rail:
  // collapsing to a 64px rail on a 360px phone is not a useful state.
  const handleToggle = (): void => {
    if (isDesktop) {
      setCollapsed((c) => !c);
      return;
    }
    setNavOpen(true);
  };

  return (
    <BreadcrumbProvider>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground"
      >
        Skip to main content
      </a>

      {/* `fixed inset-0`, not a height at all. 100vh is the LARGE viewport, so
          with the iOS URL bar showing an `h-screen` shell was ~80px taller than
          the visible area and the footer was clipped; `h-[100dvh]` fixed the
          height but the shell was still IN FLOW, so it contributed that height
          to the document and a second scrollbar could appear behind it. Out of
          flow, it contributes nothing and cannot be scrolled away from the
          viewport. See the note in AdminLayout. */}
      <div className="fixed inset-0 flex overflow-hidden bg-background text-foreground">
        {/* Rail: desktop only. Previously this was rendered unconditionally at
            240px, leaving 120px of content on a 360px phone. */}
        <div className="hidden lg:flex">
          <SalesSidebar collapsed={collapsed} />
        </div>

        {/* Drawer: phone and tablet. Radix supplies the focus trap, Escape,
            backdrop dismiss and focus restore. */}
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetContent aria-describedby={undefined}>
            <SheetTitle className="sr-only">Workspace navigation</SheetTitle>
            <SalesSidebar collapsed={false} variant="drawer" onNavigate={() => setNavOpen(false)} />
          </SheetContent>
        </Sheet>

        <div className="flex min-w-0 flex-1 flex-col">
          <SalesTopBar
            onToggleSidebar={handleToggle}
            collapsed={isDesktop ? collapsed : true}
            isDesktop={isDesktop}
          />

          {/* The width cap lives on <main> itself, not on a wrapper div around
              the Outlet: several pages size a pane with `lg:h-full`, which
              resolves against their parent, and an auto-height wrapper would
              silently collapse that to `auto` - killing the bounded scroll
              container the sticky table headers depend on. */}
          <main
            id="main-content"
            tabIndex={-1}
            // overscroll-contain: this is the workspace's scroll container, so
            // without it the leftover scroll at either end is handed to the
            // document and the whole shell moves.
            className="mx-auto w-full max-w-[1600px] flex-1 overflow-auto overscroll-contain px-3 py-3 focus:outline-none sm:px-4 sm:py-4 lg:px-6"
          >
            <Outlet />
          </main>

          <footer className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border bg-card px-4 py-1.5 text-[11px] text-muted-foreground sm:py-2">
            <span className="truncate">
              <span className="hidden sm:inline">AdvisorKhoj MIS CRM · </span>
              v0.1.0 · <span className="font-semibold text-foreground">DEV</span>
              <span className="hidden sm:inline"> · © 2026 AdvisorKhoj</span>
            </span>
            {/* Below sm this footer link is hidden and SalesSidebar's drawer
                carries the support address instead, so phones keep a route to
                support without the two rendering at the same viewport. */}
            <a
              href="mailto:support@advisorkhoj.com"
              className="hidden truncate hover:text-foreground sm:inline"
            >
              support@advisorkhoj.com
            </a>
          </footer>
        </div>
      </div>
    </BreadcrumbProvider>
  );
};

export default SalesLayout;
